import { action, observable, toJS } from 'mobx';
import { makeObservable } from 'mobx';
import {
  IFilterDTOState,
  IFilterValue,
  TFilterOp,
  TFilterPayload,
  TFilterType,
  TFilterValue,
  TVisualizationType,
} from './types';
import { FilterConditionDTO } from './FilterConditionDTO';
import { FiltersStore } from './FiltersStore';
import { Box, BoxProps, Input, InputProps } from '@apia/theme';
import { observer } from 'mobx-react-lite';
import { FC, ReactNode } from 'react';
import { getLabel } from '@apia/util';
import { Checkbox, FieldErrorMessage, IconButton } from '../components';

export class FilterDTO<T extends IFilterDTOState = IFilterDTOState> {
  type: string = 'filter';
  public state: T;
  isTd: boolean;
  ref: HTMLElement | null = null;

  constructor(params: T, filterType?: TFilterType, isTd: boolean = true) {
    this.state = params;
    filterType && (this.state.filterType = filterType);
    if (params.filterCondition && typeof params.filterCondition === 'string') {
      this.state.filterCondition = new FilterConditionDTO(
        {
          op: params.filterCondition as any as TFilterOp,
        },
        params.valueType,
        this,
      );
    }
    if (
      this.state.value &&
      (this.state.filterCondition.state.op === 'IN' ||
        this.state.filterCondition.state.op === 'NOT_IN' ||
        this.state.filterCondition.state.op === 'RANGE')
    ) {
      this.state.multipleValue = (String(this.state.value) ?? '').split(',');
      this.state.value = undefined;
    }
    this.state.visualizationType =
      params.visualizationType.toUpperCase() as TVisualizationType;
    this.state.hasFixedType = !!this.state.valueType;
    makeObservable(this, {
      state: observable,
      setValue: action,
    });
    this.isTd = isTd;
  }

  private isCheckBox() {
    return (
      this.state.filterCondition.state.op === 'NULL' ||
      this.state.filterCondition.state.op === 'NOT_NULL'
    );
  }

  getStoreValue() {
    return ['IN', 'NOT_IN', 'RANGE'].includes(
      this.state.filterCondition.state.op,
    )
      ? this.state.multipleValue?.filter((c) => !!c).join(',')
      : this.state.value;
  }

  getValue() {
    if (!this.state.value && this.isCheckBox()) {
      if (!FiltersStore.getFilter(this.state.name, 'F')) {
        this.state.value = String((this.state.value as string) === 'true');
      } else {
        this.state.value = 'true';
      }
    }
    return this.state.value;
  }

  getValueTypeLabel() {
    switch (this.state.valueType) {
      case 'D':
        return getLabel('lblDate').text;
      case 'N':
        return getLabel('lblNum').text;
      case 'S':
        return getLabel('lblStr').text;
      default:
        return this.state.valueType;
    }
  }

  setMultipleValue(value: string | Date | number, idx?: number) {
    if (!this.state.multipleValue) {
      this.state.multipleValue = [];
    }

    if (this.state.multipleValue) {
      if (idx !== undefined) {
        this.state.multipleValue[idx] = value;
      } else {
        this.state.multipleValue.push(this.state.value as TFilterValue);
      }
    }
  }

  setValue(value: string | number | Date) {
    this.state.value = value;
  }

  private resolveMultipleValueInPayload() {
    if (
      this.state.filterCondition.state.op === 'IN' ||
      this.state.filterCondition.state.op === 'NOT_IN'
    ) {
      return this.state.multipleValue?.join(',');
    } else if (this.state.filterCondition.state.op === 'RANGE') {
      return this.state.multipleValue?.slice(0, 2).join(',');
    }
  }

  asPayloadElement() {
    const aux: TFilterPayload = toJS(this.state) as any as TFilterPayload;
    aux.filterCondition = this.state.filterCondition.state.op;
    aux.multipleValue && (aux.value = this.resolveMultipleValueInPayload());

    return aux;
  }

  asFilterValue(): IFilterValue | null {
    const value = this.state.multipleValue
      ? this.resolveMultipleValueInPayload()
      : this.state.value;
    return value
      ? {
          condition: this.state.filterCondition.state.op,
          value,
          type: this.state.valueType,
        }
      : null;
  }

  public getInputType() {
    return this.state.valueType === 'N'
      ? 'number'
      : this.state.valueType === 'D'
        ? 'date'
        : 'text';
  }

  removeMultipleValue(value: TFilterValue) {
    this.state.multipleValue = (this.state.multipleValue ?? []).filter(
      (x) => x !== value,
    );
  }

  isUndefined(value: TFilterValue) {
    return !value || !(value !== 'undefined');
  }

  Component = observer(
    ({
      renderers,
      multipleInputBoxProps,
    }: {
      renderers?: {
        input?: FC<InputProps & { label?: string }>;
      };
      multipleInputBoxProps?: BoxProps;
    }) => {
      const value = this.getValue();
      const type = this.getInputType();
      let visualizationComponent: ReactNode = null;

      const error = this.state.validationError ? (
        <FieldErrorMessage>{this.state.validationError}</FieldErrorMessage>
      ) : null;

      const InputRenderer = renderers?.input || Input;

      switch (this.state.filterCondition.state.op) {
        case 'CONTAINS':
        case 'DISTINCT':
        case 'ENDS_WITH':
        case 'EQUAL':
        case 'NOT_CONTAINS':
        case 'NOT_ENDS_WITH':
        case 'NOT_STARTS_WITH':
        case 'STARTS_WITH':
        case 'GREATER_OR_EQUAL_THAN':
        case 'GREATER_THAN':
        case 'LOWER_OR_EQUAL_THAN':
        case 'LOWER_THAN': {
          visualizationComponent = (
            <InputRenderer
              onChange={(ev) => {
                this.setValue(ev.target.value);
              }}
              type={type}
              value={value as string}
              ref={(el) => (this.ref = el)}
            ></InputRenderer>
          );
          break;
        }
        case 'IN':
        case 'NOT_IN':
          visualizationComponent = (
            <Box {...multipleInputBoxProps}>
              <InputRenderer
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    this.setMultipleValue(this.getValue() as TFilterValue);
                    this.setValue('');
                  }
                }}
                onChange={(ev) => {
                  this.setValue(ev.target.value);
                  ev.preventDefault();
                }}
                type={type}
                value={value as string}
                ref={(el) => (this.ref = el)}
              ></InputRenderer>
              {[...(this.state.multipleValue ?? new Set())].map((x, idx) => (
                <Box {...multipleInputBoxProps} className="multiplesValuesBox">
                  <InputRenderer
                    onChange={(ev) => {
                      this.setMultipleValue(ev.target.value, idx);
                    }}
                    value={x as string}
                    type={type}
                  ></InputRenderer>
                  <IconButton
                    icon="Trash"
                    onClick={() => {
                      this.removeMultipleValue(x as TFilterValue);
                    }}
                  ></IconButton>
                </Box>
              ))}
            </Box>
          );
          break;
        case 'NOT_NULL':
        case 'NULL':
          // eslint-disable-next-line no-case-declarations
          const isFixed = this.state.filterType === 'F';
          isFixed && this.setValue('true');
          visualizationComponent = (
            <Checkbox
              disabled={isFixed}
              type="checkbox"
              onChange={(ev) =>
                this.setValue(ev.target.checked ? 'true' : 'false')
              }
              checked={value === 'true'}
              ref={(el) => (this.ref = el)}
            ></Checkbox>
          );
          break;
        case 'RANGE':
          if (
            this.state.multipleValue &&
            this.state.multipleValue?.length >= 0 &&
            this.isUndefined(this.state.multipleValue[0])
          ) {
            this.state.multipleValue[0] = '';
          }
          visualizationComponent = (
            <>
              <Box>
                {/*<Label>{getLabel('lblProEleFrom').text}</Label>*/}
                <InputRenderer
                  value={
                    this.state.multipleValue &&
                    this.state.multipleValue?.length >= 0 &&
                    !this.isUndefined(this.state.multipleValue[0])
                      ? (this.state.multipleValue[0] as any)
                      : ''
                  }
                  label={getLabel('lblProEleTo').text}
                  title={getLabel('lblProEleFrom').text}
                  type={type}
                  onChange={(ev) => {
                    this.setMultipleValue(ev.target.value, 0);
                  }}
                  ref={(el) => (this.ref = el)}
                ></InputRenderer>
              </Box>
              <Box>
                {/*<Label>{getLabel('lblProEleTo').text}</Label>*/}
                <InputRenderer
                  value={
                    this.state.multipleValue &&
                    this.state.multipleValue?.length >= 0 &&
                    this.state.multipleValue[1]
                      ? (this.state.multipleValue[1] as any)
                      : ''
                  }
                  label={getLabel('lblProEleTo').text}
                  title={getLabel('lblProEleTo').text}
                  type={type}
                  onChange={(ev) => {
                    if ((this.state.multipleValue ?? []).length < 1) {
                      this.setMultipleValue('', 0);
                    }
                    this.setMultipleValue(ev.target.value, 1);
                  }}
                ></InputRenderer>
              </Box>
            </>
          );
          break;
        default:
          throw new Error(
            'Unknown condition: ' + this.state.filterCondition.state.op,
          );
      }

      return (
        <Box variant="layout.design.semanticSearch.filterBox">
          {!this.isTd && <span>{this.getLabel()}</span>}
          {visualizationComponent}
          {error}
        </Box>
      );
    },
  );

  getLabel() {
    return `${this.state.title} (${this.state.filterCondition.state.label})`;
  }

  validate() {
    const storeValue = this.getStoreValue();
    const isValid =
      this.state.filterType === 'U' ||
      (storeValue !== undefined && storeValue !== '');

    this.state.validationError = isValid
      ? undefined
      : getLabel('msgReqField').text;

    return isValid;
  }

  clear() {
    this.state.value = '';
    this.state.multipleValue = [];
  }

  hasFilledValue() {
    return this.state.value || this.state.multipleValue?.some((x) => x);
  }
}
